import {
  SET_NEW_TABLE,
  ADD_NEW_TABLE,
  DELETE_TABLE,
  SET_PL_TABLES,
  SET_TABLE,
  EDIT_TABLE,
  DELETE_TABLE_ROW,
  ADD_NEW_PROPERTY,
  SET_TABLES_INITIAL_STATE,
  GET_TABLE_VALIDATION_BY_OBJECT_ID,
  SET_PL_TABLES_PAGINATION,
  SET_TABLE_SUMMARIES,
  SET_TABLE_LOAD_STATUS,
  CLEAR_CACHED_TABLES,
} from "./tables.actions";
import { TablesState } from "./tables.types";
import { SET_VERB } from "./tables.actions";
import {
  addTableSummaryIfLoaded,
  countRuleTableValidationsBySeverity,
  removeTableSummary,
  syncTableSummaryIfPresent,
  updateTableSummaryValidationCount,
} from "./tableSummaries.helpers";

const newTableInitialState = {
  TableType: null,
  propertyIds: [],
  RulesetIds: [],
};

export const tablesInitialState: TablesState = {
  byId: {},
  allIds: [],
  newTable: newTableInitialState,
  total: 0,
  page: 0,

  getting: false,
  posting: false,
  deleting: false,

  summaries: {
    status: "idle",

    data: [],
  },

  tableLoadStatus: {},
};

export function tablesReducer(
  state: TablesState = tablesInitialState,
  action: any
) {
  switch (action.type) {
    case SET_NEW_TABLE: {
      return {
        ...state,
        newTable: action.payload,
      };
    }

    case SET_VERB: {
      return {
        ...state,
        ...action.payload,
      };
    }

    case ADD_NEW_TABLE: {
      const addedIds = (action.payload.allIds as string[]).filter(
        (id) => !state.allIds.includes(id)
      );

      let summaries = state.summaries;

      for (const id of addedIds) {
        const table = action.payload.byId[id];

        if (table) {
          summaries = addTableSummaryIfLoaded(summaries, table);
        }
      }

      return {
        ...state,
        ...action.payload,
        newTable: newTableInitialState,
        summaries,
      };
    }

    case DELETE_TABLE: {
      const newAllIds = state.allIds.filter((id) => id !== action.payload.id);
      const newId = action.payload.id;
      const oldState = state.byId;
      const newById = Object.keys(oldState).reduce((object, key) => {
        if (key !== newId) {
          object[key] = oldState[key];
        }
        return object;
      }, {});

      const tableLoadStatus = { ...state.tableLoadStatus };
      delete tableLoadStatus[newId];

      return {
        ...state,
        allIds: newAllIds,
        byId: newById,
        tableLoadStatus,
        summaries: removeTableSummary(state.summaries, newId),
      };
    }

    case SET_PL_TABLES: {
      if (action.payload.page)
        return {
          ...state,
          byId: {
            ...action.payload.byId,
          },
          allIds: [...action.payload.allIds],
          total: action.payload.total,
        };

      return {
        ...state,
        newTable: newTableInitialState,
        ...action.payload,
      };
    }

    case SET_TABLE: {
      const table = action.payload.data;
      const tableId = action.payload.tableId;
      const allIds = state.allIds.includes(tableId)
        ? state.allIds
        : [...state.allIds, tableId];

      return {
        ...state,
        byId: {
          ...state.byId,
          [tableId]: table,
        },
        allIds,
        summaries: syncTableSummaryIfPresent(state.summaries, table),
      };
    }

    case EDIT_TABLE: {
      const newById = {
        ...state.byId,
        ...action.payload,
      };

      let summaries = state.summaries;

      for (const table of Object.values(action.payload)) {
        summaries = syncTableSummaryIfPresent(summaries, table as any);
      }

      return {
        ...state,
        byId: newById,
        summaries,
      };
    }

    case DELETE_TABLE_ROW: {
      const table = action.payload.table;

      return {
        ...state,
        byId: {
          ...state.byId,
          [table.id]: table,
        },
        summaries: syncTableSummaryIfPresent(state.summaries, table),
      };
    }

    case ADD_NEW_PROPERTY: {
      const table = action.payload.table;

      return {
        ...state,
        byId: {
          ...state.byId,
          [action.payload.tableId]: table,
        },
        summaries: syncTableSummaryIfPresent(state.summaries, table),
      };
    }

    case GET_TABLE_VALIDATION_BY_OBJECT_ID: {
      const objectId = action.payload.objectId;
      const validations = action.payload.validation;
      const existingTable = state.byId[objectId];
      const validationCounts = countRuleTableValidationsBySeverity(
        validations,
        objectId
      );

      return {
        ...state,
        byId: existingTable
          ? {
              ...state.byId,
              [objectId]: {
                ...existingTable,
                validations,
              },
            }
          : state.byId,
        summaries: updateTableSummaryValidationCount(
          state.summaries,
          objectId,
          validationCounts
        ),
      };
    }

    case SET_PL_TABLES_PAGINATION: {
      return {
        ...state,
        page: action.payload.page,
      };
    }

    case SET_TABLE_SUMMARIES: {
      const { status, data } = action.payload;

      return {
        ...state,
        summaries: {
          status,
          data: data !== undefined ? data : state.summaries.data,
        },
      };
    }

    case SET_TABLE_LOAD_STATUS: {
      const { tableId, status } = action.payload;

      return {
        ...state,
        tableLoadStatus: {
          ...state.tableLoadStatus,
          [tableId]: status,
        },
      };
    }

    case CLEAR_CACHED_TABLES: {
      const tableIds: string[] | undefined = action.payload?.tableIds;

      // Undefined = flush entire full-table cache (summaries untouched).
      if (tableIds === undefined) {
        return {
          ...state,
          byId: {},
          allIds: [],
          tableLoadStatus: {},
        };
      }

      if (tableIds.length === 0) {
        return state;
      }

      const idsToClear = new Set(tableIds);
      const byId = { ...state.byId };
      const tableLoadStatus = { ...state.tableLoadStatus };

      tableIds.forEach((tableId) => {
        delete byId[tableId];
        delete tableLoadStatus[tableId];
      });

      return {
        ...state,
        byId,
        allIds: state.allIds.filter((id) => !idsToClear.has(id)),
        tableLoadStatus,
      };
    }

    case SET_TABLES_INITIAL_STATE: {
      return tablesInitialState;
    }

    default:
      return state;
  }
}
